import React from 'react';
import { Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AcknowledgeButton } from './AcknowledgeButton';
import {
  getLatestAcknowledgmentTime,
  getAcknowledgmentCounts,
} from '../utils/acknowledgmentUtils';
import {
  useAlertAcknowledgements,
  useApplyAcknowledgement,
} from '../hooks/useAlertAcknowledgements';
import { selectUserGuid } from '../../../slices/commonSelectors';

export interface AcknowledgmentData {
  id: string;
  userGuid: string;
  acknowledgedAt: string | null;
  acknowledgmentRequired?: boolean;
}

interface AcknowledgmentStatusAlertsProps {
  isAlertSitrep: boolean;
  eventInstanceId?: string;
  showSummary?: boolean;
}

export const AcknowledgmentStatusAlerts: React.FC<
  AcknowledgmentStatusAlertsProps
> = ({ isAlertSitrep, eventInstanceId, showSummary = true }) => {
  const currentUserGuid = useSelector(selectUserGuid);

  const { data: acknowledgements = [], isLoading } = useAlertAcknowledgements(
    isAlertSitrep && eventInstanceId ? eventInstanceId : ''
  );
  const applyAcknowledgementMutation = useApplyAcknowledgement();

  const handleAcknowledgeAlert = () => {
    const userAcknowledgement = acknowledgements.find(
      (ack) => ack.userGuid === currentUserGuid
    );
    if (userAcknowledgement) {
      applyAcknowledgementMutation.mutate({
        acknowledgementId: userAcknowledgement.id,
      });
    }
  };

  if (!isAlertSitrep) {
    return null;
  }

  const userAcknowledgement = acknowledgements.find(
    (ack) => ack.userGuid === currentUserGuid
  );
  const needsAcknowledgement =
    userAcknowledgement && !userAcknowledgement.acknowledgedAt;
  const hasAcknowledged =
    userAcknowledgement && userAcknowledgement.acknowledgedAt;

  const { acknowledgedCount, pendingCount } =
    getAcknowledgmentCounts(acknowledgements);

  return (
    <>
      {/* Warning Alert for Required Acknowledgment */}
      {needsAcknowledgement && (
        <Alert
          variant="warning"
          className="mb-4 d-flex align-items-center justify-content-between"
        >
          <div className="d-flex align-items-center">
            <span className="material-symbols-outlined me-2">
              notification_important
            </span>
            <div>
              <strong>Alert Acknowledgment Required</strong>
              <div className="small text-muted">
                This alert requires your acknowledgment before you can continue.
              </div>
            </div>
          </div>
          <AcknowledgeButton
            onClick={handleAcknowledgeAlert}
            isLoading={applyAcknowledgementMutation.isPending}
            disabled={isLoading}
            variant="warning"
          />
        </Alert>
      )}

      {/* Success Alert for Completed Acknowledgment */}
      {hasAcknowledged && (
        <Alert variant="success" className="mb-4 d-flex align-items-center">
          <span className="material-symbols-outlined me-2">check_circle</span>
          <div>
            <strong>✓ Alert Acknowledged</strong>
            <div className="mt-1">
              <span className="badge bg-success-subtle text-success border border-success-subtle">
                <span
                  className="material-symbols-outlined me-1"
                  style={{ fontSize: '14px' }}
                >
                  schedule
                </span>
                {userAcknowledgement.acknowledgedAt
                  ? new Date(
                      userAcknowledgement.acknowledgedAt
                    ).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </Alert>
      )}

      {/* Info Alert for Acknowledgment Summary */}
      {showSummary && acknowledgements.length > 0 && (
        <Alert variant="info" className="mb-4">
          <div>
            <strong>Acknowledgment Status:</strong>
            <span className="badge bg-success ms-2">
              {acknowledgedCount} acknowledged
            </span>
            <span className="badge bg-warning ms-2">
              {pendingCount} pending
            </span>
            <div className="small text-muted mt-2">
              Total users: {acknowledgements.length}
              {acknowledgedCount > 0 && (
                <span className="ms-2">
                  • Last acknowledgment:{' '}
                  {getLatestAcknowledgmentTime(acknowledgements)}
                </span>
              )}
            </div>
          </div>
        </Alert>
      )}
    </>
  );
};
